import { useEffect, useMemo, useState } from 'react';
import type { ScheduleItem } from '@modelcast/types';
import { SOURCE_RECORDING_PROFILE } from '../video/sourceRecordingProfile';

export type RecordingStorageStatus = 'ok' | 'tight' | 'risky' | 'unknown';

interface BrowserStorageEstimate {
  checked: boolean;
  availableBytes: number | null;
  quotaBytes: number | null;
  usageBytes: number | null;
}

export interface RecordingStorageEstimate {
  status: RecordingStorageStatus;
  estimatedBytes: number;
  availableBytes: number | null;
  quotaBytes: number | null;
  usageBytes: number | null;
  cameraCount: number;
  timedPoseSeconds: number;
  hasUntimedPoses: boolean;
  unavailableReason: 'storage_api_unavailable' | 'quota_unavailable' | 'untimed_poses' | null;
}

const RECORDING_STORAGE_SAFETY_MULTIPLIER = 1.25;
const TIGHT_STORAGE_RATIO = 0.6;
const RISKY_STORAGE_RATIO = 0.85;

function getTimedPoseSeconds(schedule: ScheduleItem[]) {
  return schedule
    .filter((item) => item.type === 'pose' && item.duration > 0)
    .reduce((total, item) => total + item.duration, 0);
}

function hasUntimedPoses(schedule: ScheduleItem[]) {
  return schedule.some((item) => item.type === 'pose' && item.duration <= 0);
}

function estimateRecordingBytes(timedPoseSeconds: number, cameraCount: number) {
  const bitsPerSecond = SOURCE_RECORDING_PROFILE.videoBitsPerSecond + SOURCE_RECORDING_PROFILE.audioBitsPerSecond;
  return Math.ceil((timedPoseSeconds * Math.max(1, cameraCount) * bitsPerSecond / 8) * RECORDING_STORAGE_SAFETY_MULTIPLIER);
}

async function getBrowserStorageEstimate(): Promise<BrowserStorageEstimate> {
  if (typeof navigator === 'undefined' || !navigator.storage?.estimate) {
    return {
      checked: true,
      availableBytes: null,
      quotaBytes: null,
      usageBytes: null,
    };
  }

  const estimate = await navigator.storage.estimate();
  const quotaBytes = typeof estimate.quota === 'number' ? estimate.quota : null;
  const usageBytes = typeof estimate.usage === 'number' ? estimate.usage : null;
  const availableBytes = quotaBytes !== null && usageBytes !== null
    ? Math.max(0, quotaBytes - usageBytes)
    : null;

  return {
    checked: true,
    availableBytes,
    quotaBytes,
    usageBytes,
  };
}

export function useRecordingStorageEstimate(
  schedule: ScheduleItem[],
  modelCameraCount: number,
  enabled: boolean
): RecordingStorageEstimate {
  const timedPoseSeconds = useMemo(() => getTimedPoseSeconds(schedule), [schedule]);
  const includesUntimedPoses = useMemo(() => hasUntimedPoses(schedule), [schedule]);
  const cameraCount = Math.max(1, modelCameraCount);
  const estimatedBytes = useMemo(
    () => estimateRecordingBytes(timedPoseSeconds, cameraCount),
    [cameraCount, timedPoseSeconds]
  );
  const [storageEstimate, setStorageEstimate] = useState<BrowserStorageEstimate>({
    checked: false,
    availableBytes: null,
    quotaBytes: null,
    usageBytes: null,
  });

  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;

    async function refreshStorageEstimate() {
      try {
        const nextEstimate = await getBrowserStorageEstimate();
        if (!cancelled) {
          setStorageEstimate(nextEstimate);
        }
      } catch (error) {
        console.warn('[RecordingStorageEstimate] Browser storage estimate failed', error);
        if (!cancelled) {
          setStorageEstimate({
            checked: true,
            availableBytes: null,
            quotaBytes: null,
            usageBytes: null,
          });
        }
      }
    }

    void refreshStorageEstimate();

    return () => {
      cancelled = true;
    };
  }, [enabled, estimatedBytes]);

  if (!enabled) {
    return {
      status: 'ok',
      estimatedBytes,
      availableBytes: storageEstimate.availableBytes,
      quotaBytes: storageEstimate.quotaBytes,
      usageBytes: storageEstimate.usageBytes,
      cameraCount,
      timedPoseSeconds,
      hasUntimedPoses: includesUntimedPoses,
      unavailableReason: includesUntimedPoses ? 'untimed_poses' : null,
    };
  }

  if (includesUntimedPoses) {
    return {
      status: 'unknown',
      estimatedBytes,
      availableBytes: storageEstimate.availableBytes,
      quotaBytes: storageEstimate.quotaBytes,
      usageBytes: storageEstimate.usageBytes,
      cameraCount,
      timedPoseSeconds,
      hasUntimedPoses: true,
      unavailableReason: 'untimed_poses',
    };
  }

  if (timedPoseSeconds === 0) {
    return {
      status: 'ok',
      estimatedBytes,
      availableBytes: storageEstimate.availableBytes,
      quotaBytes: storageEstimate.quotaBytes,
      usageBytes: storageEstimate.usageBytes,
      cameraCount,
      timedPoseSeconds,
      hasUntimedPoses: false,
      unavailableReason: null,
    };
  }

  if (!storageEstimate.checked) {
    return {
      status: 'ok',
      estimatedBytes,
      availableBytes: null,
      quotaBytes: null,
      usageBytes: null,
      cameraCount,
      timedPoseSeconds,
      hasUntimedPoses: false,
      unavailableReason: null,
    };
  }

  if (typeof navigator === 'undefined' || !navigator.storage?.estimate) {
    return {
      status: 'unknown',
      estimatedBytes,
      availableBytes: null,
      quotaBytes: null,
      usageBytes: null,
      cameraCount,
      timedPoseSeconds,
      hasUntimedPoses: false,
      unavailableReason: 'storage_api_unavailable',
    };
  }

  if (storageEstimate.availableBytes === null || storageEstimate.quotaBytes === null) {
    return {
      status: 'unknown',
      estimatedBytes,
      availableBytes: storageEstimate.availableBytes,
      quotaBytes: storageEstimate.quotaBytes,
      usageBytes: storageEstimate.usageBytes,
      cameraCount,
      timedPoseSeconds,
      hasUntimedPoses: false,
      unavailableReason: 'quota_unavailable',
    };
  }

  const storageRatio = estimatedBytes / storageEstimate.availableBytes;
  const status: RecordingStorageStatus =
    estimatedBytes > storageEstimate.availableBytes || storageRatio >= RISKY_STORAGE_RATIO
      ? 'risky'
      : storageRatio >= TIGHT_STORAGE_RATIO
      ? 'tight'
      : 'ok';

  return {
    status,
    estimatedBytes,
    availableBytes: storageEstimate.availableBytes,
    quotaBytes: storageEstimate.quotaBytes,
    usageBytes: storageEstimate.usageBytes,
    cameraCount,
    timedPoseSeconds,
    hasUntimedPoses: false,
    unavailableReason: null,
  };
}

export function formatStorageBytes(bytes: number | null) {
  if (bytes === null || !Number.isFinite(bytes)) return 'unknown';

  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let value = Math.max(0, bytes);
  let unitIndex = 0;

  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }

  const decimals = value >= 10 || unitIndex === 0 ? 0 : 1;
  return `${value.toFixed(decimals)} ${units[unitIndex]}`;
}
